import type { LandingEditorVersionEntry } from '../editor/landingEditorClient.js';
import type { LandingEditorElementDefinition } from '../editor/landingEditorRegistry.js';
import type { LandingEditorElementFrame, LandingHomeSectionKey } from '../../shared/editor/landingEditorTypes.js';
import { resolveApplicationPortalBase } from '../../shared/content/landingContent.js';
import {
  landingEditorSectionLibrary,
  landingEditorSectionLibraryKeys,
} from '../../shared/editor/landingEditorSectionLibrary.js';

export { editorPages } from '../../shared/editor/landingEditorNavigation.js';
export type { EditorPageKey } from '../../shared/editor/landingEditorNavigation.js';

export const platformAdminLoginHref = () => `${resolveApplicationPortalBase()}/login/admin`;

export type EditorStatus = 'idle' | 'loading' | 'unsaved' | 'saving' | 'saved' | 'published' | 'local' | 'error';
export type EditorLeftTab = 'layers' | 'pages' | 'assets' | 'media';
export type EditorPropertiesTab = 'design' | 'prototype';
export type EditorTool = 'select' | 'hand' | 'comment';
export type EditorFrame = 'desktop' | 'mobile';
export type EditorPreviewProfileId = 'full-hd' | 'macbook-pro' | 'desktop' | 'tablet' | 'mobile';
export interface EditorPreviewProfile {
  id: EditorPreviewProfileId;
  label: string;
  width: number;
  height: number;
  editorFrame: EditorFrame;
}

export const editorPreviewProfiles: readonly EditorPreviewProfile[] = [
  { id: 'full-hd', label: 'Full HD', width: 1920, height: 1080, editorFrame: 'desktop' },
  { id: 'macbook-pro', label: 'MacBook Pro 14″', width: 1512, height: 982, editorFrame: 'desktop' },
  { id: 'desktop', label: 'Desktop', width: 1200, height: 800, editorFrame: 'desktop' },
  { id: 'tablet', label: 'Tablet', width: 768, height: 1024, editorFrame: 'desktop' },
  { id: 'mobile', label: 'Mobile', width: 390, height: 844, editorFrame: 'mobile' },
];
export type RepeatTarget = { path: string; index: number };
export type RepeatIdTarget = { prefixSegments: string[]; index: number };
export type InlineTextGeometry = {
  elementId: string;
  left: number;
  top: number;
  width: number;
  minHeight: number;
  fontFamily: string;
  fontSize: string;
  fontWeight: string;
  lineHeight: string;
  letterSpacing: string;
  color: string;
  textAlign: string;
};

export const defaultFrame = (): LandingEditorElementFrame => ({ x: 0, y: 0 });
export const clampZoom = (value: number) => Math.min(1.4, Math.max(0.35, Number(value.toFixed(2))));
export const numericSegmentPattern = /^\d+$/;

export const sectionTitle = (section: LandingHomeSectionKey) => landingEditorSectionLibrary[section].label;

export const visibleEditorSectionKeys = landingEditorSectionLibraryKeys;

export const statusLabel = (status: EditorStatus) =>
  ({
    idle: 'Bereit',
    loading: 'Lade Projekt',
    unsaved: 'Ungespeichert',
    saving: 'Speichern',
    saved: 'Gespeichert',
    published: 'Live veröffentlicht',
    local: 'Lokal gespeichert',
    error: 'Fehler',
  })[status];

export const versionKindLabel = (kind: LandingEditorVersionEntry['kind']) => (kind === 'published' ? 'Publish' : 'Draft');

export const formatVersionDate = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Unbekannter Zeitpunkt';
  return new Intl.DateTimeFormat('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

export const mutableArrayAtPath = (source: Record<string, unknown>, path: string): unknown[] | null => {
  let current: unknown = source;
  for (const segment of path.split('.')) {
    if (current === undefined || current === null) return null;
    current = (current as Record<string, unknown>)[segment];
  }
  return Array.isArray(current) ? current : null;
};

export const repeatTargetForDefinition = (definition: LandingEditorElementDefinition): RepeatTarget | null => {
  if (definition.repeatPath && definition.repeatIndex !== undefined) return { path: definition.repeatPath, index: definition.repeatIndex };
  if (!definition.path) return null;

  const segments = definition.path.split('.');
  const indexPosition = segments.findIndex(segment => numericSegmentPattern.test(segment));
  if (indexPosition <= 0 || indexPosition !== segments.length - 1) return null;
  const indexSegment = segments[indexPosition];
  if (indexSegment === undefined) return null;
  return {
    path: segments.slice(0, indexPosition).join('.'),
    index: Number(indexSegment),
  };
};

export const replaceFirstNumericSegment = (elementId: string, nextIndex: number) => {
  let replaced = false;
  return elementId
    .split('.')
    .map(segment => {
      if (replaced || !numericSegmentPattern.test(segment)) return segment;
      replaced = true;
      return String(nextIndex);
    })
    .join('.');
};

export const repeatIdTargetForElementId = (elementId: string): RepeatIdTarget | null => {
  const segments = elementId.split('.');
  const indexPosition = segments.findIndex(segment => numericSegmentPattern.test(segment));
  if (indexPosition <= 0) return null;
  const indexSegment = segments[indexPosition];
  if (indexSegment === undefined) return null;
  return {
    prefixSegments: segments.slice(0, indexPosition),
    index: Number(indexSegment),
  };
};

export const matchingRepeatFrameKey = (frameKey: string, target: RepeatIdTarget) => {
  const segments = frameKey.split('.');
  if (segments.length <= target.prefixSegments.length) return null;
  if (target.prefixSegments.some((segment, index) => segments[index] !== segment)) return null;
  const indexSegment = segments[target.prefixSegments.length];
  if (!indexSegment || !numericSegmentPattern.test(indexSegment)) return null;
  return { segments, index: Number(indexSegment) };
};

export const frameKeyWithIndex = (segments: string[], target: RepeatIdTarget, nextIndex: number) => {
  const nextSegments = [...segments];
  nextSegments[target.prefixSegments.length] = String(nextIndex);
  return nextSegments.join('.');
};

export const normalizeEditableText = (value: string) => value.replace(/\u00a0/g, ' ').replace(/\s+/g, ' ').trim();
export const hexColorPattern = /^#[0-9a-f]{6}$/i;
export const colorValue = (value: string | undefined, fallback: string) => (value && hexColorPattern.test(value) ? value : fallback);
export const optionalNumberValue = (value: string) => {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : undefined;
};
export const frameInputValue = (value: number | undefined) => (value === undefined ? '' : String(value));
export const opacityPercent = (value: number | undefined) => Math.round((value ?? 1) * 100);
export const mediaAcceptForKind = (kind: LandingEditorElementDefinition['kind']) =>
  kind === 'video'
    ? 'video/mp4,video/webm,video/quicktime'
    : kind === 'track'
      ? 'text/vtt,.vtt'
      : 'image/*';
export const mediaLabelForKind = (kind: LandingEditorElementDefinition['kind']) =>
  kind === 'video' ? 'Video' : kind === 'track' ? 'Untertitel' : 'Bild';
export const numericCssValue = (value: string) => {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
};
export const scaledCssValue = (value: string, scale: number) => {
  const numeric = numericCssValue(value);
  return numeric === null ? value : `${numeric * scale}px`;
};

export const landingAnchorSection: Record<string, LandingHomeSectionKey> = {
  top: 'hero',
  weg: 'intro',
  vorteile: 'benefits',
  kosten: 'costs',
  anerkennung: 'recognition',
  team: 'team',
  informationen: 'info',
  kontakt: 'contact',
};

// Curated icon set for the visual icon picker. Each class is written out in full (not built at
// runtime) so the iconify/Tailwind scanner emits the CSS for it — otherwise the icon would be blank.
// Covers every icon used across the content plus common extras.
export const editorIconChoices: readonly string[] = [
  'icon-[material-symbols--add-circle]', 'icon-[material-symbols--add-rounded]', 'icon-[material-symbols--alternate-email]',
  'icon-[material-symbols--approval-delegation]', 'icon-[material-symbols--arrow-forward]', 'icon-[material-symbols--arrow-outward]',
  'icon-[material-symbols--balance]', 'icon-[material-symbols--business-center]', 'icon-[material-symbols--call]',
  'icon-[material-symbols--check-circle]', 'icon-[material-symbols--clinical-notes]', 'icon-[material-symbols--contract-edit]',
  'icon-[material-symbols--construction]', 'icon-[material-symbols--corporate-fare]', 'icon-[material-symbols--diversity-1]',
  'icon-[material-symbols--diversity-3]',
  'icon-[material-symbols--fact-check]', 'icon-[material-symbols--flag]', 'icon-[material-symbols--flight-land]',
  'icon-[material-symbols--flight-takeoff]', 'icon-[material-symbols--forum]', 'icon-[material-symbols--gavel]',
  'icon-[material-symbols--groups]', 'icon-[material-symbols--handshake]', 'icon-[material-symbols--health-and-safety]',
  'icon-[material-symbols--help]', 'icon-[material-symbols--home-work]', 'icon-[material-symbols--info]',
  'icon-[material-symbols--inventory]', 'icon-[material-symbols--location-on]', 'icon-[material-symbols--mail]',
  'icon-[material-symbols--manage-search]', 'icon-[material-symbols--open-in-new]', 'icon-[material-symbols--person]',
  'icon-[material-symbols--person-check]', 'icon-[material-symbols--person-search]', 'icon-[material-symbols--public]',
  'icon-[material-symbols--restaurant]',
  'icon-[material-symbols--schedule]', 'icon-[material-symbols--school]', 'icon-[material-symbols--shield-with-heart]',
  'icon-[material-symbols--star]', 'icon-[material-symbols--storefront]', 'icon-[material-symbols--support-agent]', 'icon-[material-symbols--translate]',
  'icon-[material-symbols--verified]', 'icon-[material-symbols--verified-user]', 'icon-[material-symbols--volunteer-activism]',
  'icon-[material-symbols--workspace-premium]', 'icon-[material-symbols--medical-services]', 'icon-[material-symbols--stethoscope]',
  'icon-[material-symbols--apartment]', 'icon-[material-symbols--language]', 'icon-[material-symbols--work]',
  'icon-[material-symbols--badge]', 'icon-[material-symbols--event]', 'icon-[material-symbols--description]',
  'icon-[material-symbols--lightbulb]', 'icon-[material-symbols--rocket-launch]', 'icon-[material-symbols--thumb-up]',
  'icon-[material-symbols--favorite]', 'icon-[material-symbols--menu-book]', 'icon-[material-symbols--payments]',
  'icon-[material-symbols--savings]',
];

export const editorIconLabel = (iconClass: string) =>
  iconClass.replace('icon-[material-symbols--', '').replace(']', '').replace(/-/g, ' ');
